/**
 * Service O360-Guard - API Tracking
 * Endpoints principales:
 * - GET /api/v1/guard/profile/
 * - GET /api/v1/guard/dashboard-summary/
 * - GET /api/v1/guard/alerts/
 * - GET /api/v1/guard/sites/
 * - GET /api/v1/guard/shifts/
 * - GET /api/v1/guard/incidents/
 * - GET /api/v1/guard/daily-report/
 * - GET /api/v1/guard/daily-report/export/?date=YYYY-MM-DD&format=pdf|xlsx
 * - GET /api/v1/guard/agents/
 * - GET /api/v1/guard/agents-on-duty/
 */
import { createTrackingClient } from "../trackingApi";

function getClient(accessToken: string | undefined) {
  return createTrackingClient(accessToken);
}

export interface GuardDashboardSummary {
  agents_on_duty: number;
  incidents_open: number;
  patrols_in_progress: number;
  patrols_planned_today: number;
  patrols_completed_today: number;
}

export interface GuardAlerts {
  sla_imminent: any[];
  sla_depassement: any[];
  patrols_missed: any[];
  patrols_late: any[];
}

export interface GuardSite {
  id: number;
  name: string;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  radius_meters: number | null;
  is_active: boolean;
  created_at: string;
}

export interface GuardShift {
  id: number;
  status?: string;
  planned_start?: string;
  planned_end?: string;
  check_in_at?: string | null;
  check_out_at?: string | null;
  site_name?: string;
  post_name?: string;
  agent_name?: string;
  [key: string]: unknown;
}

export interface GuardIncident {
  id: string;
  status?: string;
  incident_type_name?: string;
  site_name?: string;
  declared_at?: string;
  severity?: string;
  [key: string]: unknown;
}

export interface GuardDailyReportData {
  date?: string;
  [key: string]: unknown;
}

export async function fetchGuardDashboardSummary(
  accessToken: string | undefined
): Promise<GuardDashboardSummary> {
  const client = getClient(accessToken);
  const { data } = await client.get<GuardDashboardSummary>("/api/v1/guard/dashboard-summary/");
  return {
    agents_on_duty: data?.agents_on_duty ?? 0,
    incidents_open: data?.incidents_open ?? 0,
    patrols_in_progress: data?.patrols_in_progress ?? 0,
    patrols_planned_today: data?.patrols_planned_today ?? 0,
    patrols_completed_today: data?.patrols_completed_today ?? 0,
  };
}

export async function fetchGuardAlerts(
  accessToken: string | undefined
): Promise<GuardAlerts> {
  const client = getClient(accessToken);
  const { data } = await client.get<GuardAlerts>("/api/v1/guard/alerts/");
  return {
    sla_imminent: data?.sla_imminent ?? [],
    sla_depassement: data?.sla_depassement ?? [],
    patrols_missed: data?.patrols_missed ?? [],
    patrols_late: data?.patrols_late ?? [],
  };
}

export async function fetchGuardSites(
  accessToken: string | undefined
): Promise<GuardSite[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<GuardSite[]>("/api/v1/guard/sites/");
  return Array.isArray(data) ? data : [];
}

export async function fetchGuardShifts(
  accessToken: string | undefined
): Promise<GuardShift[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<GuardShift[]>("/api/v1/guard/shifts/");
  return Array.isArray(data) ? data : [];
}

export async function fetchGuardIncidents(
  accessToken: string | undefined,
  params?: { status?: string; site?: string; severity?: string }
): Promise<GuardIncident[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<GuardIncident[]>("/api/v1/guard/incidents/", { params });
  return Array.isArray(data) ? data : [];
}

export interface GuardAgentOption {
  id: string;
  name: string;
}

export async function fetchGuardAgents(
  accessToken: string | undefined
): Promise<GuardAgentOption[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<GuardAgentOption[]>("/api/v1/guard/agents/");
  return Array.isArray(data) ? data : [];
}

export interface GuardPatrol {
  id: string;
  name?: string;
  status?: string;
  site_name?: string;
  started_at?: string | null;
  [key: string]: unknown;
}

export async function fetchGuardPatrols(
  accessToken: string | undefined
): Promise<GuardPatrol[]> {
  const client = getClient(accessToken);
  const res = await client.get<GuardPatrol[] | { results: GuardPatrol[] }>("/api/v1/guard/patrols/");
  const data = res.data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && "results" in data) return (data as { results: GuardPatrol[] }).results;
  return [];
}

export interface GuardIncidentType {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
}

export async function fetchGuardIncidentTypes(
  accessToken: string | undefined
): Promise<GuardIncidentType[]> {
  const client = getClient(accessToken);
  const response = await client.get<GuardIncidentType[] | { results: GuardIncidentType[] }>(
    "/api/v1/guard/incident-types/"
  );
  const data = response.data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && "results" in data)
    return (data as { results: GuardIncidentType[] }).results;
  return [];
}

export interface CreateGuardShiftPayload {
  agent_id: string;
  site_id: string;
  planned_start: string; // ISO datetime
  planned_end: string;
  post_id?: string | null;
}

export async function createGuardShift(
  accessToken: string | undefined,
  payload: CreateGuardShiftPayload
): Promise<GuardShift> {
  const client = getClient(accessToken);
  const { data } = await client.post<GuardShift>("/api/v1/guard/shifts/", payload);
  return data;
}

export interface CreateIncidentPayload {
  site_id: string;
  shift_id?: string | null;
  incident_type_id?: string | null;
  severity: string;
  description?: string;
  latitude?: number | null;
  longitude?: number | null;
}

export async function createGuardIncident(
  accessToken: string | undefined,
  payload: CreateIncidentPayload,
  files?: File[]
): Promise<GuardIncident> {
  const client = getClient(accessToken);
  const formData = new FormData();
  formData.append("site_id", payload.site_id);
  if (payload.shift_id) formData.append("shift_id", payload.shift_id);
  if (payload.incident_type_id) formData.append("incident_type_id", payload.incident_type_id);
  formData.append("severity", payload.severity);
  if (payload.description != null) formData.append("description", payload.description);
  if (payload.latitude != null) formData.append("latitude", String(payload.latitude));
  if (payload.longitude != null) formData.append("longitude", String(payload.longitude));
  if (files?.length) {
    files.forEach((f) => formData.append("files", f));
  }
  const { data } = await client.post<GuardIncident>("/api/v1/guard/incidents/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function incidentTakeOver(
  accessToken: string | undefined,
  incidentId: string
): Promise<GuardIncident> {
  const client = getClient(accessToken);
  const { data } = await client.post<GuardIncident>(
    `/api/v1/guard/incidents/${incidentId}/take-over/`,
    {}
  );
  return data;
}

export async function incidentResolve(
  accessToken: string | undefined,
  incidentId: string,
  payload?: { resolution_comment?: string }
): Promise<GuardIncident> {
  const client = getClient(accessToken);
  const { data } = await client.post<GuardIncident>(
    `/api/v1/guard/incidents/${incidentId}/resolve/`,
    payload ?? {}
  );
  return data;
}

export async function incidentReject(
  accessToken: string | undefined,
  incidentId: string
): Promise<GuardIncident> {
  const client = getClient(accessToken);
  const { data } = await client.post<GuardIncident>(
    `/api/v1/guard/incidents/${incidentId}/reject/`,
    {}
  );
  return data;
}

export async function fetchGuardDailyReport(
  accessToken: string | undefined,
  date?: string
): Promise<GuardDailyReportData> {
  const client = getClient(accessToken);
  const { data } = await client.get<GuardDailyReportData>("/api/v1/guard/daily-report/", {
    params: date ? { date } : undefined,
  });
  return data ?? {};
}

export async function exportGuardDailyReport(
  accessToken: string | undefined,
  options: { date?: string; format: "pdf" | "xlsx" }
): Promise<Blob> {
  const client = getClient(accessToken);
  const { data } = await client.get<Blob>("/api/v1/guard/daily-report/export/", {
    params: {
      ...(options.date ? { date: options.date } : {}),
      format: options.format,
    },
    responseType: "blob",
  } as any);
  return data;
}

